/**
 * Stubbed checkout services (AR-290 is front-end only).
 *
 * Each function fakes the latency of the real integration so loading/disabled
 * states are exercised for real. Deterministic triggers let QA reach every state
 * in the design without a backend:
 *
 *  Account lookup (Auth0)
 *    - email containing `existing`  → an account already exists
 *    - email containing `failsetup` → provisioning/service error
 *  USPS address verification
 *    - ZIP `00000`                  → USPS outage ("use as entered")
 *    - street with a correctable spelling/suffix → suggestion returned
 *    - otherwise unrecognised street → no match
 *  Stripe payment (standard Stripe test cards)
 *    - 4242 4242 4242 4242 → succeeds
 *    - 4000 0000 0000 0002 → declined
 *    - 4000 0000 0000 0119 → processor error / timeout
 *    - 4000 0000 0000 3220 → 3DS / SCA challenge
 *    - 4000 0000 0000 0341 → charge succeeds, provisioning then fails
 */

import type { AddressDraft } from './types';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export type AccountLookupResult =
  | { status: 'available' }
  | { status: 'exists' }
  | { status: 'error' };

export const lookupAccount = async (email: string): Promise<AccountLookupResult> => {
  await delay(500);
  const value = email.trim().toLowerCase();
  if (value.includes('failsetup')) return { status: 'error' };
  if (value.includes('existing')) return { status: 'exists' };
  return { status: 'available' };
};

export const requestPasswordReset = async (email: string): Promise<{ status: 'sent' }> => {
  await delay(400);
  void email;
  return { status: 'sent' };
};

export type AddressVerification =
  | { status: 'verified'; normalized: AddressDraft }
  | { status: 'suggestion'; suggested: AddressDraft }
  | { status: 'no_match' }
  | { status: 'unavailable' };

/** Common street-suffix and spelling fixes the USPS API would normalise for us. */
const STREET_CORRECTIONS: Array<[RegExp, string]> = [
  [/\bstreet\b/gi, 'St'],
  [/\bavenue\b/gi, 'Ave'],
  [/\broad\b/gi, 'Rd'],
  [/\bdrive\b/gi, 'Dr'],
  [/\blane\b/gi, 'Ln'],
  [/\bboulevard\b/gi, 'Blvd'],
  [/\bcourt\b/gi, 'Ct'],
  [/\bplace\b/gi, 'Pl'],
  [/\bterrace\b/gi, 'Ter'],
  [/\bcircle\b/gi, 'Cir'],
  [/\bparkway\b/gi, 'Pkwy'],
  [/\bhighway\b/gi, 'Hwy'],
  [/\bharbour\b/gi, 'Harbor'],
  [/\bvew\b/gi, 'View'],
  [/\bcentre\b/gi, 'Center'],
];

const STANDARD_SUFFIX =
  /\b(st|ave|rd|dr|ln|blvd|ct|pl|ter|cir|pkwy|hwy|way|trl|loop|run|row|walk|sq|aly|byp)\.?$/i;

export const verifyAddress = async (address: AddressDraft): Promise<AddressVerification> => {
  await delay(600);

  if (address.zip.trim() === '00000') return { status: 'unavailable' };

  const corrected = STREET_CORRECTIONS.reduce(
    (street, [pattern, replacement]) => street.replace(pattern, replacement),
    address.street.trim(),
  );

  if (corrected.toLowerCase() !== address.street.trim().toLowerCase()) {
    return {
      status: 'suggestion',
      suggested: { ...address, street: corrected, zip: `${address.zip.slice(0, 5)}-2288` },
    };
  }

  if (!STANDARD_SUFFIX.test(corrected)) return { status: 'no_match' };

  return { status: 'verified', normalized: { ...address, street: corrected } };
};

export type PaymentOutcome =
  | { status: 'succeeded' }
  | { status: 'declined'; message: string }
  | { status: 'processor_error'; message: string }
  | { status: 'requires_authentication' }
  | { status: 'provisioning_failed' };

const TEST_CARDS: Record<string, PaymentOutcome> = {
  '4000000000000002': {
    status: 'declined',
    message: 'This card was declined. Try another card or contact your bank.',
  },
  '4000000000000119': {
    status: 'processor_error',
    message: 'We could not reach the payment processor. Nothing has been charged.',
  },
  '4000000000003220': { status: 'requires_authentication' },
  '4000000000000341': { status: 'provisioning_failed' },
};

export const submitPayment = async (cardNumber: string): Promise<PaymentOutcome> => {
  await delay(900);
  return TEST_CARDS[cardNumber.replace(/\D/g, '')] ?? { status: 'succeeded' };
};

export const confirmAuthenticationChallenge = async (): Promise<{ status: 'succeeded' }> => {
  await delay(700);
  return { status: 'succeeded' };
};
